import { useEffect, useState, useRef } from 'react';
import { Video, FileText, CreditCard, Users, Brain, TrendingUp, Shield, Zap } from 'lucide-react';

const hireSphereFeatures = [
  { icon: Video, text: 'Video-based candidate assessments' },
  { icon: FileText, text: 'Customisable job posts' },
  { icon: CreditCard, text: 'Credit-based pricing (USD 0.50/credit)' },
  { icon: Users, text: 'Collaborative hiring tools' },
];

const ravenFeatures = [
  { icon: Brain, text: 'Business Memory engine' },
  { icon: TrendingUp, text: 'Finance + HR + Ops unified' },
  { icon: Zap, text: 'Real-time intelligence' },
  { icon: Shield, text: 'Predictive analytics' },
];

export default function ProductsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div id="products" ref={sectionRef} data-testid="products-section">
      {/* Product 1 - HireSphere Platform (Light Section) */}
      <section className="py-24 sm:py-32 bg-[#F4F7FD]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Mockup */}
            <div className={`reveal ${isVisible ? 'visible' : ''}`}>
              <div className="relative">
                <div className="bg-[#0D1321] rounded-3xl p-8 shadow-2xl">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="space-y-4">
                    <div className="h-8 bg-[#193E82]/30 rounded w-3/4" />
                    <div className="h-4 bg-white/10 rounded w-full" />
                    <div className="h-4 bg-white/10 rounded w-5/6" />
                    <div className="grid grid-cols-3 gap-4 mt-8">
                      <div className="h-24 bg-[#F5A623]/20 rounded-lg" />
                      <div className="h-24 bg-[#193E82]/20 rounded-lg" />
                      <div className="h-24 bg-[#F5A623]/20 rounded-lg" />
                    </div>
                  </div>
                </div>
                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 bg-[#F5A623] text-[#0D1321] text-xs font-bold px-4 py-2 rounded-full">
                  NEW
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`reveal ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
              <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
                Product
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-[#1A1A2E] mb-6">
                HireSphere Recruitment Platform
              </h2>
              <p className="text-lg leading-relaxed text-[#5A5A5A] mb-8">
                An AI-enabled, credit-based recruitment web application designed to streamline 
                your hiring process with video-based assessments and collaborative tools.
              </p>
              <ul className="space-y-4 mb-8">
                {hireSphereFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#193E82]/10 flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-[#193E82]" />
                    </div>
                    <span className="text-[#1A1A2E] font-medium">{feature.text}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid="product-hiresphere-cta"
                className="btn-primary rounded-full px-8 py-4 text-base font-semibold inline-flex items-center"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product 2 - RavenRecruit (Dark Section) */}
      <section className="py-24 sm:py-32 bg-[#0D1321] relative overflow-hidden">
        {/* Circuit Board Texture Overlay */}
        <img
          src="https://static.prod-images.emergentagent.com/jobs/651541d7-5e6b-42ae-bbbb-a15776d2b14a/images/e4999b22e6e96b63f63379e586d4e089214cb10290beac1b50287d8c22ef2507.png"
          alt=""
          loading="lazy"
          decoding="async"
          className="circuit-overlay"
          aria-hidden="true"
        />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className={`reveal ${isVisible ? 'visible' : ''} order-2 lg:order-1`}>
              <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
                Flagship Product
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-4">
                RavenRecruit
              </h2>
              <p className="text-xl text-[#F5A623] font-semibold mb-6">
                "Where Data Becomes Decisions."
              </p>
              <p className="text-lg leading-relaxed text-white/70 mb-8">
                Our flagship AI-native enterprise intelligence platform acts as your unified 
                CFO, COO, and HR system — automating compliance, forecasting, procurement, 
                and talent allocation.
              </p>
              <ul className="space-y-4 mb-8">
                {ravenFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-[#F5A623]" />
                    </div>
                    <span className="text-white font-medium">{feature.text}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid="product-raven-cta"
                className="btn-accent rounded-full px-8 py-4 text-base font-semibold inline-flex items-center"
              >
                Request Demo
              </a>
            </div>

            {/* Mockup */}
            <div className={`reveal ${isVisible ? 'visible' : ''} order-1 lg:order-2`} style={{ transitionDelay: '200ms' }}>
              <div className="relative">
                <div className="glass-card rounded-3xl p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#F5A623] to-[#193E82] rounded-2xl mx-auto flex items-center justify-center">
                      <Brain className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-white text-xl font-bold mt-4">RavenRecruit AI</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-white/60 text-sm">Finance OS</span>
                      <span className="text-[#F5A623] text-sm font-semibold">Active</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-white/60 text-sm">HR Intelligence</span>
                      <span className="text-[#F5A623] text-sm font-semibold">Active</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-white/60 text-sm">Predictive Analytics</span>
                      <span className="text-[#F5A623] text-sm font-semibold">Active</span>
                    </div>
                  </div>
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-4 -left-4 bg-[#193E82] text-white text-xs font-bold px-4 py-2 rounded-full">
                  AI-POWERED
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
